<template>
  <div class="grid-standard gap-y-16" data-test="product-card-grid">
    <div
      v-for="(product, index) in products"
      :key="product.title"
      :ref="setupViewportTrackingProduct(product, tracking)"
      class="md:col-span-2 lg:col-span-3"
    >
      <ProductCard
        :color-swatches="
          colorSwatches(product.specifications, productGranularity)
        "
        :deal="product.deal"
        :description="
          description(
            product,
            productGranularity,
            i18n(translations.specialOfferType7),
          )
        "
        :extended-description="extendedDescription(productGranularity)"
        :image-alt="product.title"
        :image-src="product.image"
        :listing-id="product.listingId"
        :price="product.price"
        :product
        :review-rating="product.reviewRating"
        :title="product.title"
        :url="product.productPageLink"
        :with-starting-from="withStartingFrom(productGranularity)"
        v-on="addToCartListener ? { addToCart: handleAddToCart } : {}"
        @link-click="
          trackProductClick(product, {
            ...tracking,
            position: index + 1,
            granularity: productGranularity,
          })
        "
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { getCurrentInstance } from 'vue'

import type { ProductGranularity } from '@backmarket/http-api/src/api-specs-content/models/refined-products'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import {
  type ExtraTrackingData,
  useClickTracking,
  useImpressionTracking,
} from '../../composables/useProductTracking'
import type { Product, Products } from '../../models/product'
import ProductCard from '../ProductCard/ProductCard.vue'

import translations from './ProductCardCarousel.translations'
import {
  colorSwatches,
  description,
  extendedDescription,
  withStartingFrom,
} from './ProductCardVersion.helper'

withDefaults(
  defineProps<{
    products: Products
    tracking: ExtraTrackingData
    productGranularity?: ProductGranularity
  }>(),
  {
    productGranularity: undefined,
  },
)

const i18n = useI18n()

const emit = defineEmits<{
  (e: 'add-to-cart', data: { status: string; product: Product }): void
}>()

const addToCartListener = getCurrentInstance()?.vnode.props?.onAddToCart

const { setupViewportTrackingProduct } = useImpressionTracking()
const { trackProductClick } = useClickTracking()

function handleAddToCart(product: Product) {
  return ({ status }: { status: string }) => {
    emit('add-to-cart', { status, product })
  }
}
</script>
